import type { NurseWithRelation } from "@/types/nurse-type";

function isEmptyOrderedNurseValue(key: keyof NurseWithRelation, value: unknown) {
  if (key !== "grade" && key !== "experience") return false;
  if (value == null) return true;
  const numericValue = Number(value);
  return Number.isNaN(numericValue) || numericValue === 0;
}

export const compareNurses = (
  a: NurseWithRelation,
  b: NurseWithRelation,
  key: keyof NurseWithRelation,
  order: "asc" | "desc"
) => {
  const rawValA = a[key];
  const rawValB = b[key];

  const valA = isEmptyOrderedNurseValue(key, rawValA) ? null : rawValA;
  const valB = isEmptyOrderedNurseValue(key, rawValB) ? null : rawValB;

  if (valA === valB) return 0;
  if (valA === null || valA === undefined) return 1; // null은 항상 뒤로
  if (valB === null || valB === undefined) return -1;

  if (typeof valA === "number" && typeof valB === "number") {
    return order === "asc" ? valA - valB : valB - valA;
  }

  if (typeof valA === "string" && typeof valB === "string") {
    // 가나다순 정렬 (로케일 비교)
    const comparison = valA.localeCompare(valB, "ko");
    return order === "asc" ? comparison : -comparison;
  }

  return 0;
};

/**
 * 그룹 이름 기준 정렬 함수
 * 한글(가나다순) → 숫자(숫자순) → 영어(알파벳순) → 모음 순서로 정렬
 * @param nameA 첫 번째 그룹 이름
 * @param nameB 두 번째 그룹 이름
 * @returns 정렬 결과 (-1, 0, 1)
 */
export const compareGroupNames = (nameA: string, nameB: string): number => {
  const a = (nameA || "").trim();
  const b = (nameB || "").trim();

  if (!a && !b) return 0;
  if (!a) return 1;
  if (!b) return -1;

  // 첫 글자 타입 확인
  const getFirstCharType = (
    str: string
  ): "hangul" | "number" | "english" | "other" => {
    if (/^[가-힣]/.test(str)) return "hangul";
    if (/^\d/.test(str)) return "number";
    if (/^[a-zA-Z]/.test(str)) return "english";
    return "other";
  };

  const typeA = getFirstCharType(a);
  const typeB = getFirstCharType(b);

  // 타입별 우선순위: 한글 > 숫자 > 영어 > 기타
  const typeOrder: Record<string, number> = {
    hangul: 1,
    number: 2,
    english: 3,
    other: 4,
  };

  // 타입이 다르면 타입 순서로 정렬
  if (typeA !== typeB) {
    return typeOrder[typeA] - typeOrder[typeB];
  }

  // 같은 타입끼리 정렬
  if (typeA === "hangul" && typeB === "hangul") {
    // 한글: 가나다순
    return a.localeCompare(b, "ko");
  }

  if (typeA === "number" && typeB === "number") {
    // 숫자: 숫자 부분 추출해서 비교
    const numMatchA = a.match(/^(\d+)/);
    const numMatchB = b.match(/^(\d+)/);

    if (numMatchA && numMatchB) {
      const numA = Number(numMatchA[1]);
      const numB = Number(numMatchB[1]);

      // 숫자가 다르면 숫자순으로 정렬
      if (numA !== numB) {
        return numA - numB;
      }

      // 숫자가 같으면 나머지 부분으로 비교
      const restA = a.slice(numMatchA[1].length);
      const restB = b.slice(numMatchB[1].length);
      return restA.localeCompare(restB, "ko");
    }
  }

  if (typeA === "english" && typeB === "english") {
    // 영어: 알파벳순
    return a.localeCompare(b, "en");
  }

  // 그 외의 경우: localeCompare로 정렬
  return a.localeCompare(b, "ko");
};
